import type { AlertItem, AlertSeverity } from '../../../api/types';
import { ALERT_SEVERITY_COLORS } from '../../../utils/colors';
import type { ChartSeries, MarkerShape, SeriesMarker } from '../seriesTypes';

const SHAPES: Readonly<Record<AlertSeverity, MarkerShape>> = {
    log: 'circle',
    info: 'circle',
    warning: 'triangle',
    critical: 'diamond',
};

const LOG_COLOR = '#888888';

export function firstOfEachRun(
    alerts: readonly AlertItem[],
    bounds: readonly [Date, Date]
): AlertItem[] {
    const chronological = [...alerts].sort(
        (a, b) => new Date(a.ts).getTime() - new Date(b.ts).getTime()
    );
    const visible: AlertItem[] = [];
    const lastKeyByMarket = new Map<string, string>();
    for (const alert of chronological) {
        const key = `${alert.severity}__${alert.alert_type}`;
        if (lastKeyByMarket.get(alert.market_id) === key) continue;
        lastKeyByMarket.set(alert.market_id, key);
        const t = new Date(alert.ts);
        if (t >= bounds[0] && t <= bounds[1]) visible.push(alert);
    }
    return visible;
}

export function alertSeries(
    alerts: readonly AlertItem[],
    bounds: [Date, Date],
    marketIds: ReadonlySet<string>,
    flat = false
): ChartSeries {
    const shown = alerts.filter(a => marketIds.has(a.market_id));
    const markers: SeriesMarker[] = firstOfEachRun(shown, bounds).map(a => ({
        id: a.id,
        t: new Date(a.ts).getTime(),
        v: flat ? null : a.current_yes_prob ?? null,
        color: ALERT_SEVERITY_COLORS[a.severity] || LOG_COLOR,
        shape: SHAPES[a.severity] ?? 'circle',
        title: a.title,
        detail: a.detail,
    }));
    return {
        id: 'alerts',
        label: 'Alerts',
        color: ALERT_SEVERITY_COLORS.info,
        unit: 'probability',
        draw: 'markers',
        axis: 'hidden',
        home: 'probability',
        movable: false,
        hasData: markers.length > 0,
        data: { kind: 'markers', markers },
    };
}
